/**
 * Asking the browser not to throw the footage away (web ticket 13).
 *
 * Origin-private storage is *best-effort* by default: a browser under disk
 * pressure may evict an origin's data without asking, and the origin whose data
 * it is finds out by it not being there. For settings that would be an
 * annoyance; for a take that has not been delivered yet it is the loss this
 * whole storage arrangement exists to prevent.
 *
 * Persistent storage is the opt-out, and it is granted rather than prompted for
 * on Chromium — the browser decides from how much the user has used the site, so
 * a first visit may well be refused and a regular user will not be. That makes
 * the answer worth having but not worth acting on: cuekards records either way,
 * because a refusal is "your data might be evicted under disk pressure" and not
 * "you cannot record".
 */

export interface PersistencePorts {
  /** Whether the origin's storage is already persistent. */
  persisted(): Promise<boolean>;
  /** Ask for it to be. */
  persist(): Promise<boolean>;
}

/**
 * Ask once, at startup, and only when there is something to ask for.
 *
 * Checked before asking rather than asked unconditionally: the answer is already
 * yes on every visit after the one that was granted, and `persist()` is a
 * permission call, which is not the kind of thing to make on every page load for
 * an answer already known.
 */
export async function askForPersistentStorage(
  ports: PersistencePorts,
): Promise<boolean> {
  try {
    if (await ports.persisted()) return true;
    return await ports.persist();
  } catch {
    // A browser without the API, or one that refuses to discuss it. Treated as
    // "not persistent", which is what it is, and nothing else changes.
    return false;
  }
}

/**
 * The same, against the browser this page is in — or a no-op where there is no
 * `StorageManager`, which is a browser this application does not support anyway.
 */
export function askThisBrowserToPersist(): Promise<boolean> {
  const storage = navigator.storage as StorageManager | undefined;
  if (!storage?.persist) return Promise.resolve(false);
  return askForPersistentStorage({
    persisted: () => storage.persisted(),
    persist: () => storage.persist(),
  });
}
